// @flow
import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArticleIcon from '@mui/icons-material/Article';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import * as DmsCommon from '../../supports/dms-common';
import DocxViewer from './DocxViewer';
import ExcelViewer from './ExcelViewer';
import TextViewer from './TextViewer';
import { isWordFile, isExcelFile, isTextFile, getFileExt } from './fileViewerUtils';

export default function DocViewerPage() {
  const [fileData, setFileData] = useState({
    url: '',
    filename: '',
    title: '',
    canDownload: true,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    let resolvedData = {
      url: '',
      filename: '',
      title: '',
      canDownload: true,
    };

    const docViewerParam = searchParams.get('docViewer');
    if (docViewerParam) {
      try {
        const decoded = DmsCommon.decodeStrFromPassing(docViewerParam);
        const parsed = JSON.parse(decoded);
        resolvedData = {
          url: parsed.url || '',
          filename: parsed.filename || '',
          title: parsed.title || parsed.filename || '',
          canDownload: parsed.canDownload !== false,
        };
      } catch (err) {
        console.error('Failed to parse docViewer payload from URL:', err);
      }
    }

    // Fallbacks if not set or query params are passed individually
    if (!resolvedData.url) {
      const url = searchParams.get('url') || searchParams.get('file') || '';
      const filename = searchParams.get('filename') || searchParams.get('name') || '';
      const title = searchParams.get('title') || filename || 'Document';
      const canDlParam = searchParams.get('canDownload');
      const canDownload = canDlParam !== '0' && canDlParam !== 'false';
      resolvedData = { url, filename, title, canDownload };
    }

    setFileData(resolvedData);

    const docTitle = resolvedData.title || resolvedData.filename || 'Document Preview';
    document.title = `${docTitle} - DMS Document Viewer`;
  }, []);

  const { url, filename, title, canDownload } = fileData;
  const ext = getFileExt(filename);
  const isWord = isWordFile(filename);
  const isExcel = isExcelFile(filename);
  const isText = isTextFile(filename);
  const isPdf = ext === '.pdf';

  const themeColor = useMemo(() => {
    if (isExcel) return '#107c41';
    if (isText) return '#0f766e';
    if (isWord) return '#185abd';
    if (isPdf) return '#dc2626';
    return '#475569';
  }, [isExcel, isText, isWord, isPdf]);

  const handleCloseTab = () => {
    window.close();
  };

  const handleDirectDownload = () => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        height: '100vh',
        bgcolor: '#0f172a',
        overflow: 'hidden',
      }}
    >
      {/* Top Header Bar */}
      <Box
        sx={{
          height: 56,
          px: 2.5,
          bgcolor: '#0f172a',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #334155',
          flexShrink: 0,
          zIndex: 100,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, mr: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: themeColor,
              color: '#ffffff',
              flexShrink: 0,
            }}
          >
            {isExcel && <TableChartIcon fontSize="small" />}
            {isText && <ArticleIcon fontSize="small" />}
            {isWord && <DescriptionIcon fontSize="small" />}
            {isPdf && <PictureAsPdfIcon fontSize="small" />}
            {!isExcel && !isText && !isWord && !isPdf && <DescriptionIcon fontSize="small" />}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="subtitle1"
                noWrap
                sx={{
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '1rem',
                  maxWidth: { xs: 240, sm: 500, md: 800 },
                }}
              >
                {title || filename || 'Document Preview'}
              </Typography>

              {ext && (
                <Chip
                  label={ext.replace('.', '').toUpperCase()}
                  size="small"
                  sx={{
                    bgcolor: themeColor,
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              )}
            </Box>

            {filename && title !== filename && (
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: '#94a3b8',
                  display: 'block',
                  fontSize: '0.75rem',
                  maxWidth: { xs: 240, sm: 500, md: 800 },
                }}
              >
                {filename}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Top Header Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {canDownload && url && (
            <Tooltip title="Download file to computer">
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDirectDownload}
                sx={{
                  color: '#e2e8f0',
                  borderColor: '#475569',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                Download
              </Button>
            </Tooltip>
          )}

          <Tooltip title="Close Tab (Ctrl+W)">
            <IconButton
              size="small"
              onClick={handleCloseTab}
              sx={{
                color: '#94a3b8',
                '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)' },
                ml: 0.5,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Viewer Body */}
      <Box
        sx={{
          flexGrow: 1,
          height: 'calc(100vh - 56px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#1e293b',
          position: 'relative',
        }}
      >
        {isWord && url && (
          <DocxViewer
            fileUrl={url}
            filename={filename}
            canDownload={canDownload}
          />
        )}

        {isExcel && url && (
          <ExcelViewer
            fileUrl={url}
            filename={filename}
            canDownload={canDownload}
          />
        )}

        {isText && url && (
          <TextViewer
            fileUrl={url}
            filename={filename}
            canDownload={canDownload}
          />
        )}

        {isPdf && url && (
          <iframe
            src={`/pdfviewer/web/viewer.html?file=${encodeURIComponent(url)}&disableDownload=${canDownload ? 'false' : 'true'}`}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title={title || filename || 'PDF Viewer'}
          />
        )}

        {!isWord && !isExcel && !isText && !isPdf && url && (
          <Box
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
              Unsupported document preview format
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
              In-browser preview is available for Word (.docx), Excel (.xlsx, .xls, .csv), Text (.txt), and PDF documents.
            </Typography>
            {canDownload && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDirectDownload}
                sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
              >
                Download File
              </Button>
            )}
          </Box>
        )}

        {!url && (
          <Box
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography variant="h6" sx={{ color: '#ef4444', mb: 1 }}>
              No document specified
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              The viewer URL did not contain valid document parameters.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
